'use client'

import { type HTMLAttributes, useRef } from 'react'

import cn from 'clsx'

import css from './indicator-dots.module.css'

export type IndicatorDotsProps = HTMLAttributes<HTMLDivElement> & {
    labels: string[]
    activeIndex?: number
    onSelectIndex?: (index: number) => void
    disabled?: boolean
}

export function IndicatorDots({
    labels,
    activeIndex = 0,
    onSelectIndex,
    className,
    disabled,
    ...rest
}: IndicatorDotsProps) {
    const dotButtonRefs = useRef<(HTMLButtonElement | null)[]>(Array(labels.length))

    function onKeyDown(event: React.KeyboardEvent<HTMLButtonElement>) {
        switch (event.key) {
            case 'ArrowRight': {
                const next = activeIndex < labels.length - 1 ? activeIndex + 1 : 0
                if (onSelectIndex) {
                    onSelectIndex?.(next)
                    dotButtonRefs.current[next]?.focus()
                }
                break
            }
            case 'ArrowLeft': {
                const next = activeIndex > 0 ? activeIndex - 1 : labels.length - 1
                onSelectIndex?.(next)
                dotButtonRefs.current[next]?.focus()
                break
            }
        }
    }

    return (
        <nav className={cn(css.indicatorDots, { [css.disabled]: disabled }, className)} {...rest}>
            <div role="tablist">
                {labels.map((label, idx) => (
                    <div
                        key={`bullet-indicator-${idx}-${label}`}
                        role="tab"
                        aria-selected={activeIndex === idx ? 'true' : 'false'}
                    >
                        <button
                            ref={(el) => {
                                dotButtonRefs.current[idx] = el
                            }}
                            type="button"
                            title={label}
                            aria-label={label}
                            onClick={() => onSelectIndex?.(idx)}
                            tabIndex={!disabled && activeIndex === idx ? 0 : -1}
                            onKeyDown={onKeyDown}
                            disabled={disabled}
                        />
                    </div>
                ))}
            </div>
        </nav>
    )
}
